import type { Variants } from '@amplitude/experiment-js-client';
import { env } from '@/constants';
import { computed, ref } from 'vue';
import { FeatureFlagKeys } from '@/model/feature-flags/FeatureFlags';
import { getAmplitudeProxyService } from '@art-basel/artbasel-lib-platform';

const getAmplitudeStorageKey = (): string => {
  const last6DigitsOfApiKey = env.VITE_AMPLITUDE_CLIENT_KEY?.slice(-6) || '';
  return `amp-exp-$default_instance-${last6DigitsOfApiKey}`;
};
const amplitudeStorageKey = getAmplitudeStorageKey();

type FeatureFlagValueMap = Partial<Record<FeatureFlagKeys, boolean>>;

const featureFlagsValues = ref<FeatureFlagValueMap | undefined>(undefined);
const publicFeatureFlagsValues = ref<FeatureFlagValueMap | undefined>(undefined);

const isVariantRecord = (value: unknown): value is Variants => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const parseVariants = (rawValue: string | undefined): Variants => {
  if (!rawValue) {
    return {};
  }

  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    return isVariantRecord(parsedValue) ? parsedValue : {};
  } catch {
    return {};
  }
};

const readAmplitudeLocalStorage = (type?: string): string | undefined => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const storageKey = type ? `${amplitudeStorageKey}-${type}` : amplitudeStorageKey;
    return window.localStorage.getItem(storageKey) || undefined;
  } catch {
    console.error('Failed to read amplitude feature flags from localStorage');
  }
};

const toFeatureFlagsObject = (
  variants: Variants,
): FeatureFlagValueMap => {
  const flags: FeatureFlagValueMap = {};

  Object.values(FeatureFlagKeys).forEach((key) => {
    const variantValue = variants[key]?.value;
    if (variantValue === 'on' || variantValue === 'off') {
      flags[key] = variantValue === 'on';
    }
  });

  return flags;
};

const refreshFeatureFlags = () => {
  const variants = parseVariants(readAmplitudeLocalStorage());
  const publicVariants = parseVariants(readAmplitudeLocalStorage('public'));

  featureFlagsValues.value = toFeatureFlagsObject(variants);
  publicFeatureFlagsValues.value = toFeatureFlagsObject(publicVariants);
};

const onStorage = (event: StorageEvent) => {
  if (event.key === amplitudeStorageKey || event.key === `${amplitudeStorageKey}-public`) {
    refreshFeatureFlags();
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', onStorage);
}

refreshFeatureFlags();

export const useAmplitudeFeatureFlags = () => {
  const featureFlags = computed(() => {
    const mergedFlags = {
      ...publicFeatureFlagsValues.value,
      ...featureFlagsValues.value,
    };

    return {
      // We can hardcode/overwrite feature flags values here
      // TODO - Migrate the hardcoded field names to use FeatureFlagKeys insteand.
      isCopyItemEnabled: mergedFlags[FeatureFlagKeys.COPY_ITEM] ?? false,
      isDigitalPortfolioEnabled: false,
      isItemTransferEnabled: false,
      isPaymentSettingsEnabled: mergedFlags[FeatureFlagKeys.PAYMENT_SETTINGS] ?? false,
      isNotificationsEnabled: mergedFlags[FeatureFlagKeys.NOTIFICATIONS] ?? false,
      isMarkAsSoldEnabled: false,
      isTeamEnabled: mergedFlags[FeatureFlagKeys.TEAM] ?? false,
      isViewingRoomsEnabled: mergedFlags[FeatureFlagKeys.VIEWING_ROOMS] ?? false,
      isLinkInBioEnabled: false,
      isCurrencyConfigurationEnabled: mergedFlags[FeatureFlagKeys.CURRENCY_CONFIGURATION] ?? false,
      isVideoArtworkEnabled: true,
      isSectorsEnabled: mergedFlags[FeatureFlagKeys.SECTORS] ?? false,
      isMagicLinkEnabled: mergedFlags[FeatureFlagKeys.MAGIC_LINK_LOGIN] ?? false,
      isChunkedUploadEnabled: mergedFlags[FeatureFlagKeys.CHUNKED_ART_UPLOAD] ?? false,
      isCollaboratorsEnabled: mergedFlags[FeatureFlagKeys.COLLABORATORS] ?? false,
      isArtistsEnabled: mergedFlags[FeatureFlagKeys.ARTISTS] ?? false,
      isGalleryProfileEnabled: mergedFlags[FeatureFlagKeys.GALLERY_PROFILE] ?? false,
      isAuth0MigrationEnabled: mergedFlags[FeatureFlagKeys.AUTH0_MIGRATION] ?? false,
    };
  });

  const setAmplitudeLocalStorageFlags = (value: Variants, type?: string) => {
    const last6DigitsOfApiKey = env.VITE_AMPLITUDE_CLIENT_KEY?.slice(-6) || '';
    const amplitudeCookieKey = `amp-exp-$default_instance-${last6DigitsOfApiKey}${type ? `-${type}` : ''}`;
    localStorage.setItem(amplitudeCookieKey, JSON.stringify(value));
    refreshFeatureFlags();
  };

  const clearAmplitudeLocalStorageFlags = () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.removeItem(amplitudeStorageKey);
    } catch {
      console.error('Failed to clear amplitude feature flags from localStorage');
    }

    refreshFeatureFlags();
  };

  const getSecureAmplitudeFeatureFlags = async () => {
    try {
      const initialVariants: Variants = await getAmplitudeProxyService().getSecureFeatureFlags();
      setAmplitudeLocalStorageFlags(initialVariants);
    } catch (e) {
      console.error('Failed to fetch feature flags from Amplitude Proxy', e);
    }
  };

  const getPublicAmplitudeFeatureFlags = async () => {
    try {
      const initialVariants: Variants = await getAmplitudeProxyService().getFeatureFlags();
      setAmplitudeLocalStorageFlags(initialVariants, 'public');
    } catch (e) {
      console.error('Failed to fetch feature flags from Amplitude Proxy', e);
    }
  };

  return {
    featureFlags,
    refreshFeatureFlags,
    clearAmplitudeLocalStorageFlags,
    getSecureAmplitudeFeatureFlags,
    getPublicAmplitudeFeatureFlags,
  };
};
